// Package Imports
import { z } from 'zod'

const questionCategorySchema = z.object({
  id: z.string(),
  name: z.string(),
})

const lobbyTournamentInfoSchema = z.object({
  id: z.string(),
  name: z.string().catch('-'),
  status: z.string().catch(''),
  match_completed: z.boolean().catch(false),
})

const lobbyLeagueInfoSchema = z.object({
  id: z.string(),
  name: z.string().catch('-'),
  is_activated: z.boolean().catch(false),
})

const lobbySchema = z.object({
  id: z.string(),
  name: z.string(),
  tournament: lobbyTournamentInfoSchema.nullish().catch(null),
  league: lobbyLeagueInfoSchema.nullish().catch(null),
})

const participantSchema = z.object({
  name: z.string(),
  avatar: z.string().catch(''),
  is_in_lobby: z.boolean().catch(false),
  ready: z.boolean().catch(false),
})

const questionConfigSchema = z.object({
  question_count: z.number(),
  question_time: z.number(),
  question_difficulty: z.string().catch('medium'),
  score_per_correct_answer: z.number().catch(0),
  question_categories: z.array(questionCategorySchema).optional(),
})

const gameInstanceSchema = z.object({
  index: z.number(),
  game_config: z.record(z.string(), z.unknown()).optional(),
  question_configs: z
    .array(z.unknown())
    .optional()
    .transform((arr) =>
      arr
        ? arr
            .map((q) => questionConfigSchema.safeParse(q))
            .filter((r) => r.success)
            .map((r) => r.data)
        : undefined
    ),
})

const filterValid = <T extends z.ZodTypeAny>(item: T) =>
  z.array(z.unknown()).transform((arr) =>
    arr
      .map((x) => item.safeParse(x))
      .filter((r) => r.success)
      .map((r) => r.data as z.infer<T>)
  )

export const lobbyDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  lobby_type: z.string().catch('public'),
  admin_user: z.string().catch('-'),
  is_team_enabled: z.boolean().catch(false),
  is_active: z.boolean().catch(true),
  participants: filterValid(participantSchema).catch([]),
  game_instances: filterValid(gameInstanceSchema).catch([]),
})

export const paginationSchema = z.object({
  page: z.number().catch(1),
  per_page: z.number().catch(10),
  total: z.number().catch(0),
  total_page: z.number().catch(1),
})

export const lobbyListResponseSchema = z.object({
  data: filterValid(lobbySchema).catch([]),
  meta: z
    .object({
      pagination: paginationSchema.optional(),
    })
    .optional(),
})
